import { useEffect, useState, type ReactNode } from "react";
import { FolderClosed, FolderPlus, Inbox } from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@app/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@app/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";

/**
 * The classeur dialogs, shared by the laboratory's datasets (PT-112) and the catalogue's cohorts
 * (PT-162). Each page says what its classeurs hold and who sees them, and brings its own API calls.
 */

/** The value the radio group uses for « Sans classeur » — Radix forbids an empty one. */
const NO_FOLDER = "__none__";

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export interface NamedFolder {
  id: string;
  name: string;
}

/** Names a classeur: a new one, or one being renamed. */
export function FolderNameDialog({
  open,
  onOpenChange,
  initialName,
  description,
  placeholder,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Empty for a new classeur. */
  initialName: string;
  /** What a classeur holds, and who sees it. */
  description: ReactNode;
  /** An example name, in the page's own words. */
  placeholder: string;
  onSubmit: (name: string) => Promise<void>;
}) {
  const { t } = useLingui();
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const renaming = initialName !== "";

  useEffect(() => {
    if (open) {
      setName(initialName);
      setError(null);
    }
  }, [open, initialName]);

  const submit = async () => {
    if (!name.trim()) {
      setError(t`Le nom du classeur est requis`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(name.trim());
      onOpenChange(false);
    } catch (err) {
      setError(errorText(err, t`Erreur lors de l'enregistrement`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {renaming ? (
              <Trans>Renommer le classeur</Trans>
            ) : (
              <Trans>Nouveau classeur</Trans>
            )}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-2 py-2"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Label htmlFor="folder-name">
            <Trans>Nom</Trans>
          </Label>
          <Input
            id="folder-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholder}
            disabled={saving}
            autoFocus
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
        </form>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            <Trans>Annuler</Trans>
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? (
              <Trans>Enregistrement...</Trans>
            ) : renaming ? (
              <Trans>Renommer</Trans>
            ) : (
              <Trans>Créer</Trans>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Files an item in one of the classeurs offered, a new one, or « Sans classeur ». */
export function MoveToFolderDialog({
  item,
  folders,
  description,
  placeholder,
  createFolder,
  fileItem,
  onClose,
  onMoved,
}: {
  /** The item being filed, with the classeur it sits in; null closes the dialog. */
  item: { folder_id: string | null } | null;
  /** The classeurs it may go in. */
  folders: NamedFolder[];
  description: ReactNode;
  /** An example name for a new classeur. */
  placeholder: string;
  createFolder: (name: string) => Promise<NamedFolder>;
  fileItem: (folderId: string | null) => Promise<void>;
  onClose: () => void;
  onMoved: (folderId: string | null) => void;
}) {
  const { t } = useLingui();
  const [choice, setChoice] = useState(NO_FOLDER);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (item) {
      setChoice(item.folder_id ?? NO_FOLDER);
      setNewName("");
      setError(null);
    }
  }, [item]);

  const save = async () => {
    if (!item) return;
    setSaving(true);
    setError(null);
    try {
      let folderId: string | null = choice === NO_FOLDER ? null : choice;
      if (newName.trim()) {
        folderId = (await createFolder(newName.trim())).id;
      }
      await fileItem(folderId);
      onMoved(folderId);
      onClose();
    } catch (err) {
      setError(errorText(err, t`Erreur lors de l'enregistrement`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={item !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Ranger dans un classeur</Trans>
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <RadioGroup
          value={newName.trim() ? "" : choice}
          onValueChange={(value) => {
            setChoice(value);
            setNewName("");
          }}
          className="max-h-64 overflow-y-auto py-1"
        >
          {folders.map((folder) => (
            <Label
              key={folder.id}
              className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 px-3 py-2 font-normal hover:border-primary/50"
            >
              <RadioGroupItem value={folder.id} />
              <FolderClosed className="h-4 w-4 text-primary" />
              <span className="truncate">{folder.name}</span>
            </Label>
          ))}
          <Label className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 px-3 py-2 font-normal hover:border-primary/50">
            <RadioGroupItem value={NO_FOLDER} />
            <Inbox className="h-4 w-4 text-slate-500" />
            <Trans>Sans classeur</Trans>
          </Label>
        </RadioGroup>
        <div className="space-y-2">
          <Label
            htmlFor="folder-new"
            className="flex items-center gap-2 text-slate-600"
          >
            <FolderPlus className="h-4 w-4" />
            <Trans>Ou un nouveau classeur</Trans>
          </Label>
          <Input
            id="folder-new"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={placeholder}
            disabled={saving}
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            <Trans>Annuler</Trans>
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? <Trans>Enregistrement...</Trans> : <Trans>Ranger</Trans>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Deletes a classeur; what it holds is kept and goes back to « Sans classeur ». */
export function DeleteFolderDialog({
  folder,
  description,
  deleteFolder,
  onClose,
  onDeleted,
}: {
  folder: NamedFolder | null;
  /** Says what becomes of what it holds. */
  description: ReactNode;
  deleteFolder: (id: string) => Promise<void>;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { t } = useLingui();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (folder) setError(null);
  }, [folder]);

  const remove = async () => {
    if (!folder) return;
    setDeleting(true);
    try {
      await deleteFolder(folder.id);
      onDeleted();
      onClose();
    } catch (err) {
      setError(errorText(err, t`Erreur lors de la suppression`));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AlertDialog
      open={folder !== null}
      onOpenChange={(open) => !open && onClose()}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <Trans>Supprimer le classeur</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>
            <Trans>Annuler</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void remove();
            }}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          >
            {deleting ? (
              <Trans>Suppression...</Trans>
            ) : (
              <Trans>Supprimer</Trans>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
